import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Skeleton,
    Stack, TextField, Typography, useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apikeyActions } from '../actions';
import { workerConstants } from '../constants';

// Personal API key management (one key per user). The raw key only ever lives
// in this component's local state, for the one-time reveal - it is never
// dispatched, stored in Redux or logged (see apikey.actions.js).

const BRAND = '#166fe5';
const DIALOG_Z_INDEX = 10000000000; // same convention as the other Menu dialogs
const PUBLIC_DISCUSSION_URL = workerConstants.WORKER + 'public/discussion';
const MONO = '"SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace';
const COPIED_MS = 2000;

const CURL_SNIPPET =
    "curl -X POST '" + PUBLIC_DISCUSSION_URL + "' \\\n" +
    "  -H 'X-API-Key: olk_YOUR_KEY' \\\n" +
    "  -F 'discussion={\"space\":2,\"title\":\"Hello\",\"post\":\"From the API\"};type=application/json' \\\n" +
    "  -F \"image=@photo.jpg\"";

const MESSAGES = {
    session: "Your session has expired. Please sign in again.",
    blocked: "Your account is suspended or banned, so you can't create an API key right now.",
    unavailable: "API keys aren't available yet. Please try again later.",
    network: "Can't reach OneLike. Check your connection and try again.",
    generic: "Something went wrong on our side. Please try again."
};

// Map by HTTP status only (403 is only meaningful for POST).
function errorMessage(error) {
    if (!error) return null;
    if (error.status === 401) return MESSAGES.session;
    if (error.status === 403 && error.op === 'generate') return MESSAGES.blocked;
    if (error.status === 404) return MESSAGES.unavailable;
    if (error.status === 0) return MESSAGES.network;
    return MESSAGES.generic;
}

// Backend dates look like "2026-09-27T14:03:12.345+02" - moment parses the
// hour-only offset; new Date() does not reliably.
function formatDate(value) {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.format('D MMM YYYY, HH:mm') : null;
}

function fromNow(value) {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.fromNow() : null;
}

const visuallyHidden = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0
};

function selectElementText(el) {
    if (!el) return;
    if (typeof el.select === 'function') {
        el.focus();
        el.select();
        return;
    }
    const selection = window.getSelection && window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);
}

function writeClipboard(text) {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        return navigator.clipboard.writeText(text);
    }
    return Promise.reject(new Error('Clipboard unavailable'));
}

export const ApiKeyDialog = ({ open, onClose, myAppreciation }) => {
    const dispatch = useDispatch();
    const mode = useSelector(state => state.themeswitch) === 'dark' ? 'dark' : 'light';
    const { loading, mutating, error, key } = useSelector(state => state.apikey);
    const blocked = !!(myAppreciation && (myAppreciation.suspended || myAppreciation.banned));

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode,
            primary: { main: BRAND },
            background: mode === 'dark' ? { paper: '#0D1F2D', default: '#0D1F2D' } : { paper: '#fff', default: '#fff' }
        }
    }), [mode]);

    const dark = mode === 'dark';
    const colors = {
        text: dark ? '#fff' : '#000',
        muted: dark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)',
        surface: dark ? 'rgba(255,255,255,0.06)' : '#f4f6f9',
        border: dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
        // brand blue lightened on the dark background for readable text contrast
        accent: dark ? '#7fb2ff' : BRAND,
        accentSoft: dark ? 'rgba(22,111,229,0.22)' : 'rgba(22,111,229,0.10)'
    };

    const fullScreen = useMediaQuery('(max-width:599.95px)');

    // Local-only secret + UI state
    const [revealedKey, setRevealedKey] = useState(null);
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const [snippetCopied, setSnippetCopied] = useState(false);
    const [snippetCopyFailed, setSnippetCopyFailed] = useState(false);
    const [announcement, setAnnouncement] = useState('');
    // Kind is kept separately from the open flag so the copy doesn't flip
    // while the confirm dialog animates closed.
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirm, setConfirm] = useState('revoke'); // 'regenerate' | 'revoke'
    const openConfirm = (kind) => { setConfirm(kind); setConfirmOpen(true); };
    const closeConfirm = () => setConfirmOpen(false);

    const keyInputRef = useRef(null);
    const snippetRef = useRef(null);
    const timers = useRef([]);
    const mounted = useRef(true);

    const later = useCallback((fn, ms) => {
        timers.current.push(setTimeout(fn, ms));
    }, []);

    // Fetch on open; wipe secret + store slice on unmount.
    useEffect(() => {
        mounted.current = true;
        dispatch(apikeyActions.get());
        return () => {
            mounted.current = false;
            timers.current.forEach(clearTimeout);
            timers.current = [];
            dispatch(apikeyActions.clear());
        };
    }, [dispatch]);

    const wipeReveal = () => {
        setRevealedKey(null);
        setCopied(false);
        setCopyFailed(false);
        setAnnouncement('');
    };

    const handleClose = (event, reason) => {
        // While the one-time key is on screen only "I've saved my key" closes it.
        if (revealedKey) return;
        if (mutating) return;
        if (reason === 'backdropClick' || reason === 'escapeKeyDown' || reason === undefined) {
            onClose();
        }
    };

    const reveal = (rawKey) => {
        if (!mounted.current || !rawKey) return;
        setCopied(false);
        setCopyFailed(false);
        setRevealedKey(rawKey);
    };

    const handleGenerate = () => {
        if (mutating) return;
        dispatch(apikeyActions.generate())
            .then(reveal)
            .catch(() => { /* error is in the store and rendered as an Alert */ });
    };

    const handleConfirm = () => {
        if (mutating) return;
        if (confirm === 'regenerate') {
            dispatch(apikeyActions.generate())
                .then(rawKey => {
                    if (!mounted.current) return;
                    closeConfirm();
                    reveal(rawKey);
                })
                .catch(() => { if (mounted.current) closeConfirm(); });
        } else if (confirm === 'revoke') {
            dispatch(apikeyActions.revoke())
                .then(() => { if (mounted.current) closeConfirm(); })
                .catch(() => { if (mounted.current) closeConfirm(); });
        }
    };

    const handleCopyKey = () => {
        if (!revealedKey) return;
        writeClipboard(revealedKey)
            .then(() => {
                if (!mounted.current) return;
                setCopyFailed(false);
                setCopied(true);
                setAnnouncement('API key copied to clipboard');
                later(() => { if (mounted.current) setCopied(false); }, COPIED_MS);
            })
            .catch(() => {
                if (!mounted.current) return;
                setCopyFailed(true);
                selectElementText(keyInputRef.current);
            });
    };

    const handleCopySnippet = () => {
        writeClipboard(CURL_SNIPPET)
            .then(() => {
                if (!mounted.current) return;
                setSnippetCopyFailed(false);
                setSnippetCopied(true);
                setAnnouncement('Example command copied to clipboard');
                later(() => { if (mounted.current) setSnippetCopied(false); }, COPIED_MS);
            })
            .catch(() => {
                if (!mounted.current) return;
                setSnippetCopyFailed(true);
                selectElementText(snippetRef.current);
            });
    };

    const errorText = errorMessage(error);
    const showSkeleton = !revealedKey && (loading || (key === undefined && !error));
    const getFailedWithoutData = !revealedKey && !loading && key === undefined && error && error.op === 'get';

    const primaryButtonSx = {
        bgcolor: BRAND,
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 20,
        px: 2.5,
        '&:hover': { bgcolor: '#1260c9' }
    };

    const blockedNote = (
        <Typography variant="body2" sx={{ color: colors.muted, mt: 1 }}>
            {MESSAGES.blocked}
        </Typography>
    );

    // ---------- sections ----------

    const errorAlert = errorText ? (
        <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={error.op === 'get' ? (
                <Button color="inherit" size="small" onClick={() => dispatch(apikeyActions.get())} disabled={loading}>
                    Retry
                </Button>
            ) : null}
        >
            {errorText}
        </Alert>
    ) : null;

    const skeleton = (
        <Box aria-busy="true" aria-label="Loading your API key">
            <Skeleton variant="rounded" height={44} sx={{ mb: 1.5 }} />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="rounded" height={36} width={180} sx={{ mt: 2 }} />
        </Box>
    );

    const emptyState = (
        <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ py: 2 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: colors.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <VpnKeyIcon aria-hidden="true" sx={{ fontSize: 32, color: colors.accent }} />
            </Box>
            <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '1.05rem' }}>
                You don't have an API key yet
            </Typography>
            <Typography variant="body2" sx={{ color: colors.muted, maxWidth: 420 }}>
                Let apps and AI agents publish discussions — with images — to OneLike on your behalf.
            </Typography>
            <Typography variant="body2" sx={{ color: colors.muted, maxWidth: 420 }}>
                Keys have a daily post limit (a rolling 24 hours), so a runaway script can't flood your Space.
            </Typography>
            <Box sx={{ pt: 1 }}>
                <Button
                    variant="contained"
                    onClick={handleGenerate}
                    disabled={blocked || mutating}
                    startIcon={mutating ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : <VpnKeyIcon aria-hidden="true" />}
                    sx={primaryButtonSx}
                >
                    {mutating ? 'Generating…' : 'Generate API key'}
                </Button>
            </Box>
            {blocked ? blockedNote : null}
        </Stack>
    );

    const revealView = (
        <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
                Copy your key now. For your security, it won't be shown again.
            </Alert>
            <Typography component="label" htmlFor="apikey-reveal-input" variant="body2" sx={{ color: colors.muted, display: 'block', mb: 0.75 }}>
                Your new API key
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                <TextField
                    id="apikey-reveal-input"
                    inputRef={keyInputRef}
                    value={revealedKey || ''}
                    fullWidth
                    multiline
                    size="small"
                    InputProps={{ readOnly: true }}
                    inputProps={{
                        spellCheck: false,
                        autoComplete: 'off',
                        'aria-describedby': copyFailed ? 'apikey-copy-fallback' : undefined,
                        style: { fontFamily: MONO, fontSize: '0.9rem', wordBreak: 'break-all', lineHeight: 1.5 }
                    }}
                    onFocus={e => e.target.select()}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.surface } }}
                />
                <Button
                    variant="contained"
                    onClick={handleCopyKey}
                    startIcon={copied ? <CheckIcon aria-hidden="true" /> : <ContentCopyIcon aria-hidden="true" />}
                    sx={{ ...primaryButtonSx, borderRadius: 1, minWidth: 110, height: 40, flexShrink: 0 }}
                >
                    {copied ? 'Copied' : 'Copy'}
                </Button>
            </Stack>
            {copyFailed ? (
                <Typography id="apikey-copy-fallback" variant="body2" sx={{ color: colors.muted, mt: 1 }}>
                    Couldn't copy automatically. The key is selected — press Ctrl+C (Cmd+C on Mac) to copy.
                </Typography>
            ) : null}
            <Typography variant="body2" sx={{ color: colors.muted, mt: 1.5 }}>
                Send it in the <Box component="code" sx={{ fontFamily: MONO, color: colors.text }}>X-API-Key</Box> header. Treat it like a password: anyone with this key can post as you.
            </Typography>
        </Box>
    );

    const detailRow = (label, value) => (
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="baseline" sx={{ py: 0.75, flexWrap: 'wrap', rowGap: 0.25 }}>
            <Typography component="dt" variant="body2" sx={{ color: colors.muted }}>{label}</Typography>
            <Typography component="dd" variant="body2" sx={{ color: colors.text, m: 0, textAlign: 'right', fontWeight: 500 }}>{value}</Typography>
        </Stack>
    );

    const detailsView = key && key.exists ? (
        <Box>
            <Box sx={{ bgcolor: colors.surface, border: '1px solid', borderColor: colors.border, borderRadius: 2, p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <VpnKeyIcon aria-hidden="true" sx={{ color: colors.accent }} />
                    <Box component="code" sx={{ fontFamily: MONO, fontSize: '1rem', color: colors.text, wordBreak: 'break-all' }}>
                        <span aria-hidden="true">{key.prefix}••••••••</span>
                        <Box component="span" sx={visuallyHidden}>Key starting with {key.prefix}</Box>
                    </Box>
                </Stack>
                <Box component="dl" sx={{ m: 0 }}>
                    {detailRow('Created', formatDate(key.createdon) || '—')}
                    {detailRow('Last used', key.lastusedat
                        ? <time dateTime={key.lastusedat} title={formatDate(key.lastusedat) || undefined}>{fromNow(key.lastusedat) || formatDate(key.lastusedat) || '—'}</time>
                        : 'Never')}
                    {detailRow('Plan', (
                        <Chip
                            size="small"
                            label={key.limited === false ? 'Unlimited' : `Limited · ${key.postsperday} posts / 24 h`}
                            sx={{ bgcolor: colors.accentSoft, color: colors.accent, fontWeight: 600 }}
                        />
                    ))}
                    {key.remaining !== null && key.remaining !== undefined
                        ? detailRow('Remaining', `${key.remaining} of ${key.postsperday} left (rolling 24 h)`)
                        : null}
                </Box>
            </Box>
            {blocked ? blockedNote : null}
        </Box>
    ) : null;

    const howToUse = (
        <Accordion
            disableGutters
            elevation={0}
            sx={{ mt: 2.5, bgcolor: 'transparent', border: '1px solid', borderColor: colors.border, borderRadius: 2, '&:before': { display: 'none' } }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon aria-hidden="true" />} aria-controls="apikey-howto-content" id="apikey-howto-header">
                <Typography sx={{ color: colors.text, fontWeight: 600 }}>How to use</Typography>
            </AccordionSummary>
            <AccordionDetails id="apikey-howto-content" sx={{ pt: 0 }}>
                <Typography variant="body2" sx={{ color: colors.muted, mb: 1 }}>
                    Publish a discussion with an image:
                </Typography>
                <Box sx={{ position: 'relative' }}>
                    <Box
                        component="pre"
                        ref={snippetRef}
                        tabIndex={0}
                        aria-label="Example curl command"
                        sx={{
                            m: 0, p: 1.5, pr: 6, fontFamily: MONO, fontSize: '0.78rem', lineHeight: 1.6,
                            color: colors.text, bgcolor: colors.surface, border: '1px solid', borderColor: colors.border,
                            borderRadius: 1, overflowX: 'auto', whiteSpace: 'pre'
                        }}
                    >
                        {CURL_SNIPPET}
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleCopySnippet}
                        aria-label={snippetCopied ? 'Example command copied' : 'Copy example command'}
                        sx={{ position: 'absolute', top: 6, right: 6, color: snippetCopied ? colors.accent : colors.muted }}
                    >
                        {snippetCopied ? <CheckIcon fontSize="small" aria-hidden="true" /> : <ContentCopyIcon fontSize="small" aria-hidden="true" />}
                    </IconButton>
                </Box>
                {snippetCopyFailed ? (
                    <Typography variant="body2" sx={{ color: colors.muted, mt: 1 }}>
                        Couldn't copy automatically. The command is selected — press Ctrl+C (Cmd+C on Mac) to copy.
                    </Typography>
                ) : null}
                <Box component="ul" sx={{ color: colors.muted, fontSize: '0.875rem', pl: 2.5, mt: 1.5, mb: 1.5 }}>
                    <li>Replace <Box component="code" sx={{ fontFamily: MONO, color: colors.text }}>olk_YOUR_KEY</Box> with your key and pick a <Box component="code" sx={{ fontFamily: MONO, color: colors.text }}>space</Box>. Space 1 is moderator-only.</li>
                    <li>When your daily limit is reached, the API answers <Box component="code" sx={{ fontFamily: MONO, color: colors.text }}>429</Box> with a <Box component="code" sx={{ fontFamily: MONO, color: colors.text }}>Retry-After</Box> header.</li>
                </Box>
                <Box
                    component="a"
                    href={PUBLIC_DISCUSSION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: colors.accent, fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', '&:hover, &:focus-visible': { textDecoration: 'underline', color: colors.accent } }}
                >
                    Full API reference (spaces, fields, errors)
                    <OpenInNewIcon aria-hidden="true" sx={{ fontSize: 16 }} />
                    <Box component="span" sx={visuallyHidden}>(opens in a new tab)</Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    );

    let body;
    if (revealedKey) {
        body = <>{revealView}{howToUse}</>;
    } else if (showSkeleton) {
        body = skeleton;
    } else if (getFailedWithoutData) {
        body = null; // the error Alert (with Retry) is the whole content
    } else if (key && key.exists) {
        body = <>{detailsView}{howToUse}</>;
    } else {
        body = <>{emptyState}{howToUse}</>;
    }

    let actions = null;
    if (revealedKey) {
        actions = (
            <Button variant="contained" onClick={wipeReveal} startIcon={<CheckIcon aria-hidden="true" />} sx={primaryButtonSx}>
                I've saved my key
            </Button>
        );
    } else if (!showSkeleton && key && key.exists) {
        actions = (
            <>
                <Button
                    variant="text"
                    color="error"
                    onClick={() => openConfirm('revoke')}
                    disabled={mutating}
                    startIcon={<DeleteOutlineIcon aria-hidden="true" />}
                    sx={{ textTransform: 'none', fontWeight: 600, mr: 'auto' }}
                >
                    Revoke
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => openConfirm('regenerate')}
                    disabled={mutating || blocked}
                    startIcon={<AutorenewIcon aria-hidden="true" />}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 20, color: colors.accent, borderColor: colors.accent, '&:hover': { borderColor: colors.accent, bgcolor: colors.accentSoft } }}
                >
                    Regenerate
                </Button>
            </>
        );
    }

    const confirmCopy = confirm === 'regenerate'
        ? {
            title: 'Regenerate API key?',
            text: 'Your current key stops working immediately. Apps using it must be updated. Your daily limit is not reset.',
            action: 'Regenerate',
            pending: 'Regenerating…'
        }
        : {
            title: 'Revoke API key?',
            text: 'Apps using this key will stop working immediately.',
            action: 'Revoke',
            pending: 'Revoking…'
        };

    return (
        <ThemeProvider theme={muiTheme}>
            <Dialog
                open={open}
                onClose={handleClose}
                disableEscapeKeyDown={!!revealedKey}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="sm"
                aria-labelledby="apikey-dialog-title"
                style={{ zIndex: DIALOG_Z_INDEX }}
                PaperProps={{ sx: { bgcolor: dark ? '#0D1F2D' : '#fff', backgroundImage: 'none', color: colors.text, borderRadius: fullScreen ? 0 : 3 } }}
            >
                <DialogTitle id="apikey-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 7, color: colors.text, fontWeight: 700 }}>
                    <VpnKeyIcon aria-hidden="true" sx={{ color: colors.accent }} />
                    Your API key
                </DialogTitle>
                {!revealedKey ? (
                    <IconButton
                        aria-label="Close"
                        onClick={() => handleClose(null, undefined)}
                        disabled={mutating}
                        sx={{ position: 'absolute', right: 12, top: 12, color: colors.muted }}
                    >
                        <CloseIcon aria-hidden="true" />
                    </IconButton>
                ) : null}
                <DialogContent sx={{ pt: 0.5 }}>
                    {errorAlert}
                    {body}
                </DialogContent>
                {actions ? (
                    <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                        {actions}
                    </DialogActions>
                ) : null}
                <Box role="status" aria-live="polite" sx={visuallyHidden}>{announcement}</Box>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={() => { if (!mutating) closeConfirm(); }}
                disableEscapeKeyDown={mutating}
                aria-labelledby="apikey-confirm-title"
                aria-describedby="apikey-confirm-text"
                maxWidth="xs"
                style={{ zIndex: DIALOG_Z_INDEX }}
                PaperProps={{ sx: { bgcolor: dark ? '#0D1F2D' : '#fff', backgroundImage: 'none', color: colors.text, borderRadius: 3, m: 2 } }}
            >
                <DialogTitle id="apikey-confirm-title" sx={{ color: colors.text, fontWeight: 700 }}>
                    {confirmCopy.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="apikey-confirm-text" sx={{ color: colors.muted }}>
                        {confirmCopy.text}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button autoFocus onClick={closeConfirm} disabled={mutating} sx={{ textTransform: 'none', fontWeight: 600, color: colors.text }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirm}
                        disabled={mutating}
                        aria-busy={mutating}
                        startIcon={mutating ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : null}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 20, px: 2.5 }}
                    >
                        {mutating ? confirmCopy.pending : confirmCopy.action}
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};
